import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { FaHistory } from 'react-icons/fa'
import styles from './styles.module.scss'
import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { Confirm } from 'notiflix/build/notiflix-confirm-aio'
import api from '../../../helpers/apiRequester'
import type { CognitoUser } from './index'
import { FALLBACK_ORGANISATIONS, mergeOrganisations } from './organisations'

interface UserDetailModalProps {
    user: CognitoUser | null
    isOpen: boolean
    onClose: () => void
    onUserUpdated: () => void
    // Orgs already present on users loaded by the parent page. Unioned with
    // the backend list so every org that currently exists can be picked.
    existingOrganisations?: string[]
}

// Password validation helpers
const PASSWORD_REQUIREMENTS = {
    minLength: 8,
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: true,
}

const generateCompliantPassword = (): string => {
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
    const lowercase = 'abcdefghijklmnopqrstuvwxyz'
    const numbers = '0123456789'
    const special = '!@#$%^&*'

    let password = ''
    password += uppercase[Math.floor(Math.random() * uppercase.length)]
    password += lowercase[Math.floor(Math.random() * lowercase.length)]
    password += numbers[Math.floor(Math.random() * numbers.length)]
    password += special[Math.floor(Math.random() * special.length)]

    const allChars = uppercase + lowercase + numbers + special
    for (let i = password.length; i < 12; i++) {
        password += allChars[Math.floor(Math.random() * allChars.length)]
    }

    return password
        .split('')
        .sort(() => Math.random() - 0.5)
        .join('')
}

const validatePassword = (password: string): { valid: boolean; errors: string[] } => {
    const errors: string[] = []

    if (password.length < PASSWORD_REQUIREMENTS.minLength) {
        errors.push(`At least ${PASSWORD_REQUIREMENTS.minLength} characters`)
    }
    if (PASSWORD_REQUIREMENTS.requireUppercase && !/[A-Z]/.test(password)) {
        errors.push('At least one uppercase letter')
    }
    if (PASSWORD_REQUIREMENTS.requireLowercase && !/[a-z]/.test(password)) {
        errors.push('At least one lowercase letter')
    }
    if (PASSWORD_REQUIREMENTS.requireNumbers && !/[0-9]/.test(password)) {
        errors.push('At least one number')
    }
    if (PASSWORD_REQUIREMENTS.requireSpecialChars && !/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) {
        errors.push('At least one special character')
    }

    return { valid: errors.length === 0, errors }
}

interface ExtraOrgMembership {
    organisation: string
    grantedAt: string
    grantedBy: string | null
}

// Sentinel users already pass `getAuthorisedRoutes('Sentinel') === allPaths`
// on the backend, so they don't need extra memberships — granting them would
// be a no-op and confuse the audit trail. The UI surfaces a notice instead
// of the picker for them.
const isSentinelUser = (organisation: string | undefined | null) => organisation === 'Sentinel'

export const UserDetailModal = ({
    user,
    isOpen,
    onClose,
    onUserUpdated,
    existingOrganisations = [],
}: UserDetailModalProps) => {
    const [isEditing, setIsEditing] = useState(false)
    const [serverOrganisations, setServerOrganisations] = useState<string[]>([])
    const organisations = useMemo(
        () => mergeOrganisations(serverOrganisations, existingOrganisations),
        [serverOrganisations, existingOrganisations],
    )
    const [isNewOrg, setIsNewOrg] = useState(false)
    const [newOrgName, setNewOrgName] = useState('')
    const [formData, setFormData] = useState({
        name: '',
        organisation: '',
        admin: false,
    })
    const [isSubmitting, setIsSubmitting] = useState(false)

    // Additional org memberships (stored in dashboard_user_org_memberships,
    // *not* in Cognito). The Cognito `custom:organisation` remains the user's
    // primary org; rows here grant access on top of that.
    const [extraOrgs, setExtraOrgs] = useState<ExtraOrgMembership[]>([])
    const [orgToGrant, setOrgToGrant] = useState('')
    const [isGrantingOrg, setIsGrantingOrg] = useState(false)
    const [orgsLoading, setOrgsLoading] = useState(false)

    // Password reset state
    const [showPasswordReset, setShowPasswordReset] = useState(false)
    const [newPassword, setNewPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [passwordValidation, setPasswordValidation] = useState<{ valid: boolean; errors: string[] }>({
        valid: false,
        errors: [],
    })
    const [isResettingPassword, setIsResettingPassword] = useState(false)
    const [passwordResetSuccess, setPasswordResetSuccess] = useState(false)
    const [copiedPassword, setCopiedPassword] = useState(false)

    useEffect(() => {
        if (user) {
            setFormData({
                name: user.name || '',
                organisation: user.organisation || '',
                admin: user.admin || false,
            })
            setIsEditing(false)
            setIsNewOrg(false)
            setNewOrgName('')
            // Reset password state
            setShowPasswordReset(false)
            setNewPassword('')
            setShowPassword(false)
            setPasswordValidation({ valid: false, errors: [] })
            setPasswordResetSuccess(false)
            setCopiedPassword(false)
            // Reset and reload extra orgs whenever a different user is opened.
            setExtraOrgs([])
            setOrgToGrant('')
            if (user.cognitoId && !isSentinelUser(user.organisation)) {
                loadExtraOrgs(user.cognitoId)
            }
        }
    }, [user])

    useEffect(() => {
        loadOrganisations()
    }, [])

    const loadOrganisations = async () => {
        try {
            const response = await api({
                url: 'cognito-organisations',
                data: {},
            })
            if (response.data.success) {
                setServerOrganisations(response.data.organisations)
            } else {
                setServerOrganisations(FALLBACK_ORGANISATIONS)
            }
        } catch (error) {
            console.error('Failed to load organisations:', error)
            setServerOrganisations(FALLBACK_ORGANISATIONS)
        }
    }

    const loadExtraOrgs = async (cognitoId: string) => {
        if (!cognitoId) return
        setOrgsLoading(true)
        try {
            const response = await api({
                url: 'dashboard-user-orgs',
                data: { cognitoId },
            })
            if (response.data.success) {
                setExtraOrgs(response.data.memberships || [])
            }
        } catch (error) {
            console.error('Failed to load additional org memberships:', error)
        } finally {
            setOrgsLoading(false)
        }
    }

    const handleGrantOrg = async () => {
        if (!user || !orgToGrant) return
        setIsGrantingOrg(true)
        try {
            const response = await api({
                url: 'dashboard-user-orgs-grant',
                data: { cognitoId: user.cognitoId, organisation: orgToGrant },
            })
            if (response.data.success) {
                if (response.data.alreadyExisted) {
                    Report.info('Already Granted', `${user.email} already has access to ${orgToGrant}.`, 'Okay')
                } else {
                    Report.success(
                        'Access Granted',
                        `${user.email} can now access ${orgToGrant} after their next login.`,
                        'Okay',
                    )
                }
                setOrgToGrant('')
                await loadExtraOrgs(user.cognitoId)
            } else {
                Report.failure('Grant Failed', response.data.error || 'Failed to grant org access.', 'Okay')
            }
        } catch (error: any) {
            console.error('Error granting org access:', error)
            Report.failure('Grant Failed', error.message || 'Failed to grant org access.', 'Okay')
        } finally {
            setIsGrantingOrg(false)
        }
    }

    const handleRevokeOrg = async (organisation: string) => {
        if (!user) return
        Confirm.show(
            'Revoke Access',
            `Remove ${organisation} access from ${user.email}? They will lose access at their next login.`,
            'Yes, Revoke',
            'Cancel',
            async () => {
                try {
                    const response = await api({
                        url: 'dashboard-user-orgs-revoke',
                        data: { cognitoId: user.cognitoId, organisation },
                    })
                    if (response.data.success) {
                        Report.success(
                            'Access Revoked',
                            `${organisation} access has been removed from ${user.email}.`,
                            'Okay',
                        )
                        await loadExtraOrgs(user.cognitoId)
                    } else {
                        Report.failure('Revoke Failed', response.data.error || 'Failed to revoke access.', 'Okay')
                    }
                } catch (error: any) {
                    console.error('Error revoking org access:', error)
                    Report.failure('Revoke Failed', error.message || 'Failed to revoke access.', 'Okay')
                }
            },
        )
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
        }))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!user) return

        const organisation = isNewOrg ? newOrgName.trim() : formData.organisation

        if (!formData.name) {
            Report.warning('Validation Error', 'Name is required.', 'Okay')
            return
        }

        setIsSubmitting(true)
        Loading.circle('Updating user...')

        try {
            const response = await api({
                url: 'cognito-users-update',
                data: {
                    email: user.email,
                    name: formData.name,
                    organisation: organisation,
                    admin: formData.admin,
                },
            })

            Loading.remove()

            if (response.data.success) {
                Report.success('User Updated', 'User details have been updated successfully.', 'Okay', () => {
                    setIsEditing(false)
                    onUserUpdated()
                })
            } else {
                Report.failure('Update Failed', response.data.error || 'Failed to update user.', 'Okay')
            }
        } catch (error: any) {
            Loading.remove()
            console.error('Error updating user:', error)
            Report.failure('Update Failed', error.message || 'Failed to update user.', 'Okay')
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleToggleStatus = async () => {
        if (!user) return

        const action = user.enabled ? 'disable' : 'enable'

        Confirm.show(
            `${action.charAt(0).toUpperCase() + action.slice(1)} User`,
            `Are you sure you want to ${action} this user?`,
            'Yes',
            'No',
            async () => {
                try {
                    Loading.circle(`${action.charAt(0).toUpperCase() + action.slice(1)}ing user...`)
                    const response = await api({
                        url: 'cognito-users-update-status',
                        data: {
                            email: user.email,
                            enabled: !user.enabled,
                        },
                    })
                    Loading.remove()

                    if (response.data.success) {
                        Report.success('Success', response.data.message, 'Okay', () => {
                            onUserUpdated()
                        })
                    } else {
                        Report.failure('Error', response.data.error || `Failed to ${action} user.`, 'Okay')
                    }
                } catch (error: any) {
                    Loading.remove()
                    console.error(`Error ${action}ing user:`, error)
                    Report.failure('Error', `Failed to ${action} user. Please try again.`, 'Okay')
                }
            },
        )
    }

    const handleDeleteUser = async () => {
        if (!user) return

        Confirm.show(
            'Delete User',
            `This will permanently delete ${user.email} from Cognito and the dashboard. This cannot be undone. Are you sure?`,
            'Yes, Delete',
            'Cancel',
            async () => {
                try {
                    Loading.circle('Deleting user...')
                    const response = await api({
                        url: 'cognito-users-delete',
                        data: {
                            email: user.email,
                            cognitoId: user.cognitoId,
                        },
                    })
                    Loading.remove()

                    if (response.data.success) {
                        Report.success('User Deleted', `${user.email} has been permanently deleted.`, 'Okay', () => {
                            onUserUpdated()
                            onClose()
                        })
                    } else {
                        Report.failure('Delete Failed', response.data.message || response.data.error || 'Failed to delete user.', 'Okay')
                    }
                } catch (error: any) {
                    Loading.remove()
                    console.error('Error deleting user:', error)
                    Report.failure(
                        'Delete Failed',
                        error.response?.data?.message || error.message || 'Failed to delete user.',
                        'Okay',
                    )
                }
            },
        )
    }

    const handleCancel = () => {
        if (user) {
            setFormData({
                name: user.name || '',
                organisation: user.organisation || '',
                admin: user.admin || false,
            })
        }
        setIsNewOrg(false)
        setNewOrgName('')
        setIsEditing(false)
        // Reset password state
        setShowPasswordReset(false)
        setNewPassword('')
        setShowPassword(false)
        setPasswordValidation({ valid: false, errors: [] })
        setPasswordResetSuccess(false)
        setCopiedPassword(false)
    }

    const handlePasswordChange = (value: string) => {
        setNewPassword(value)
        setPasswordValidation(validatePassword(value))
        setPasswordResetSuccess(false)
        setCopiedPassword(false)
    }

    const handleGeneratePassword = () => {
        const password = generateCompliantPassword()
        setNewPassword(password)
        setPasswordValidation(validatePassword(password))
        setShowPassword(true)
        setPasswordResetSuccess(false)
        setCopiedPassword(false)
    }

    const handleCopyPassword = async () => {
        try {
            await navigator.clipboard.writeText(newPassword)
            setCopiedPassword(true)
            setTimeout(() => setCopiedPassword(false), 2000)
        } catch (err) {
            console.error('Failed to copy:', err)
        }
    }

    const handleResetPassword = async () => {
        if (!user || !newPassword) return

        const validation = validatePassword(newPassword)
        if (!validation.valid) {
            Report.warning('Password Requirements', 'Password must have: ' + validation.errors.join(', '), 'Okay')
            return
        }

        Confirm.show(
            'Reset Password',
            `Are you sure you want to reset the password for ${user.email}?`,
            'Yes, Reset',
            'Cancel',
            async () => {
                setIsResettingPassword(true)
                Loading.circle('Resetting password...')

                try {
                    const response = await api({
                        url: 'cognito-users-reset-password',
                        data: {
                            email: user.email,
                            newPassword: newPassword,
                        },
                    })

                    Loading.remove()

                    if (response.data.success) {
                        setPasswordResetSuccess(true)
                        Report.success(
                            'Password Reset',
                            'Password has been reset successfully. Please share the new password with the user.',
                            'Okay',
                        )
                    } else {
                        Report.failure('Reset Failed', response.data.error || 'Failed to reset password.', 'Okay')
                    }
                } catch (error: any) {
                    Loading.remove()
                    console.error('Error resetting password:', error)
                    Report.failure('Reset Failed', error.message || 'Failed to reset password.', 'Okay')
                } finally {
                    setIsResettingPassword(false)
                }
            },
        )
    }

    const formatDate = (dateString: string) => {
        if (!dateString) return '-'
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        })
    }

    const getStatusLabel = (status: string, enabled: boolean) => {
        if (!enabled) return 'Disabled'
        switch (status) {
            case 'CONFIRMED':
                return 'Active'
            case 'UNCONFIRMED':
                return 'Unconfirmed'
            case 'FORCE_CHANGE_PASSWORD':
                return 'Pending Setup'
            default:
                return status
        }
    }

    if (!isOpen || !user) return null

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h3>{isEditing ? 'Edit User' : 'User Details'}</h3>
                    <button className={styles.modalClose} onClick={onClose}>
                        ×
                    </button>
                </div>

                <div className={styles.modalBody}>
                    {isEditing ? (
                        <form onSubmit={handleSubmit} className={styles.editForm}>
                            <div className={styles.detailRow}>
                                <span className={styles.detailLabel}>Email</span>
                                <span className={styles.detailValue}>{user.email}</span>
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="name">
                                    Name <span className={styles.required}>*</span>
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                    disabled={isSubmitting}
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="organisation">Organisation</label>
                                {isNewOrg ? (
                                    <div className={styles.newOrgWrapper}>
                                        <input
                                            type="text"
                                            id="newOrganisation"
                                            value={newOrgName}
                                            onChange={(e) => setNewOrgName(e.target.value)}
                                            placeholder="Enter new organisation name"
                                            disabled={isSubmitting}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsNewOrg(false)
                                                setNewOrgName('')
                                            }}
                                            className={styles.switchOrgButton}
                                            disabled={isSubmitting}
                                        >
                                            Select Existing
                                        </button>
                                    </div>
                                ) : (
                                    <div className={styles.newOrgWrapper}>
                                        <select
                                            id="organisation"
                                            name="organisation"
                                            value={formData.organisation}
                                            onChange={handleInputChange}
                                            disabled={isSubmitting}
                                        >
                                            <option value="">Select an organisation</option>
                                            {organisations.map((org) => (
                                                <option key={org} value={org}>
                                                    {org}
                                                </option>
                                            ))}
                                        </select>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsNewOrg(true)
                                                setFormData((prev) => ({ ...prev, organisation: '' }))
                                            }}
                                            className={styles.switchOrgButton}
                                            disabled={isSubmitting}
                                        >
                                            + New
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className={styles.formGroupCheckbox}>
                                <label>
                                    <input
                                        type="checkbox"
                                        name="admin"
                                        checked={formData.admin}
                                        onChange={handleInputChange}
                                        disabled={isSubmitting}
                                    />
                                    Admin User
                                </label>
                                <span className={styles.helpText}>Admin users have elevated permissions</span>
                            </div>

                            {/* Password Reset Section */}
                            <div className={styles.passwordResetSection}>
                                <button
                                    type="button"
                                    onClick={() => setShowPasswordReset(!showPasswordReset)}
                                    className={styles.passwordResetToggle}
                                    disabled={isSubmitting || isResettingPassword}
                                >
                                    {showPasswordReset ? '− Hide Password Reset' : '🔑 Reset Password'}
                                </button>

                                {showPasswordReset && (
                                    <div className={styles.passwordResetForm}>
                                        <div className={styles.formGroup}>
                                            <label htmlFor="newPassword">New Password</label>
                                            <div className={styles.passwordWrapper}>
                                                <input
                                                    type={showPassword ? 'text' : 'password'}
                                                    id="newPassword"
                                                    value={newPassword}
                                                    onChange={(e) => handlePasswordChange(e.target.value)}
                                                    placeholder="Enter new password"
                                                    disabled={isResettingPassword}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    className={styles.passwordToggle}
                                                    disabled={isResettingPassword}
                                                    title={showPassword ? 'Hide password' : 'Show password'}
                                                >
                                                    {showPassword ? '👁️' : '👁️‍🗨️'}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleGeneratePassword}
                                                    className={styles.generateButton}
                                                    disabled={isResettingPassword}
                                                >
                                                    Generate
                                                </button>
                                            </div>
                                            {newPassword && !passwordValidation.valid && (
                                                <div className={styles.passwordRequirements}>
                                                    <span className={styles.requirementsLabel}>Missing:</span>
                                                    <ul>
                                                        {passwordValidation.errors.map((error, idx) => (
                                                            <li key={idx}>{error}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                            {newPassword && passwordValidation.valid && (
                                                <span className={styles.passwordValid}>
                                                    ✓ Password meets all requirements
                                                </span>
                                            )}
                                        </div>

                                        <div className={styles.passwordResetActions}>
                                            {newPassword && passwordValidation.valid && (
                                                <button
                                                    type="button"
                                                    onClick={handleCopyPassword}
                                                    className={styles.copyPasswordButton}
                                                    disabled={isResettingPassword}
                                                >
                                                    {copiedPassword ? '✓ Copied' : 'Copy Password'}
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                onClick={handleResetPassword}
                                                className={styles.resetPasswordButton}
                                                disabled={
                                                    isResettingPassword || !newPassword || !passwordValidation.valid
                                                }
                                            >
                                                {isResettingPassword ? 'Resetting...' : 'Reset Password'}
                                            </button>
                                        </div>

                                        {passwordResetSuccess && (
                                            <div className={styles.passwordResetSuccess}>
                                                ✓ Password has been reset. Make sure to copy and share it with the user.
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className={styles.formActions}>
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className={styles.cancelButton}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </button>
                                <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
                                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    ) : (
                        <>
                            <div className={styles.detailsGrid}>
                                <div className={`${styles.detailRow} ${styles.fullWidth}`}>
                                    <span className={styles.detailLabel}>Email</span>
                                    <span className={styles.detailValue}>{user.email}</span>
                                </div>

                                <div className={styles.detailRow}>
                                    <span className={styles.detailLabel}>Name</span>
                                    <span className={styles.detailValue}>{user.name || '-'}</span>
                                </div>

                                <div className={styles.detailRow}>
                                    <span className={styles.detailLabel}>Organisation</span>
                                    <span className={styles.detailValue}>
                                        {user.organisation ? (
                                            <span className={styles.orgBadge}>{user.organisation}</span>
                                        ) : (
                                            '-'
                                        )}
                                    </span>
                                </div>

                                <div className={styles.detailRow}>
                                    <span className={styles.detailLabel}>Status</span>
                                    <span className={styles.detailValue}>
                                        <span
                                            className={`${styles.badge} ${
                                                !user.enabled
                                                    ? styles.disabled
                                                    : user.status === 'CONFIRMED'
                                                      ? styles.confirmed
                                                      : user.status === 'FORCE_CHANGE_PASSWORD'
                                                        ? styles.pending
                                                        : styles.unconfirmed
                                            }`}
                                        >
                                            {getStatusLabel(user.status, user.enabled)}
                                        </span>
                                    </span>
                                </div>

                                <div className={styles.detailRow}>
                                    <span className={styles.detailLabel}>Admin</span>
                                    <span className={styles.detailValue}>
                                        {user.admin ? (
                                            <span className={`${styles.badge} ${styles.confirmed}`}>Yes</span>
                                        ) : (
                                            <span className={styles.badge}>No</span>
                                        )}
                                    </span>
                                </div>

                                <div className={styles.detailRow}>
                                    <span className={styles.detailLabel}>Email Verified</span>
                                    <span className={styles.detailValue}>{user.emailVerified ? 'Yes' : 'No'}</span>
                                </div>

                                <div className={styles.detailRow}>
                                    <span className={styles.detailLabel}>Created</span>
                                    <span className={styles.detailValue}>{formatDate(user.createdAt)}</span>
                                </div>

                                <div className={styles.detailRow}>
                                    <span className={styles.detailLabel}>Last Modified</span>
                                    <span className={styles.detailValue}>{formatDate(user.lastModified)}</span>
                                </div>

                                <div className={styles.detailRow}>
                                    <span className={styles.detailLabel}>Last Login</span>
                                    <span className={styles.detailValue}>
                                        {user.lastLogin ? formatDate(user.lastLogin) : 'Never'}
                                    </span>
                                </div>

                                <div className={`${styles.detailRow} ${styles.fullWidth}`}>
                                    <span className={styles.detailLabel}>Username (Cognito)</span>
                                    <span className={styles.detailValue}>
                                        <code className={styles.codeText}>{user.username}</code>
                                    </span>
                                </div>
                            </div>

                            {/* Additional org memberships — granted on top of the user's
                                primary Cognito org. Stored in
                                `dashboard_user_org_memberships`; takes effect on the
                                user's next login. */}
                            <div className={styles.extraOrgsSection}>
                                <div className={styles.extraOrgsHeader}>
                                    <h4>Additional Organisations</h4>
                                    <span className={styles.extraOrgsHelp}>
                                        Grant this user access to additional dashboards on top of their primary
                                        organisation. Takes effect at their next login.
                                    </span>
                                </div>

                                {isSentinelUser(user.organisation) ? (
                                    <div className={styles.extraOrgsNotice}>
                                        Sentinel users already have access to every organisation — no additional
                                        memberships are needed.
                                    </div>
                                ) : (
                                    <>
                                        {orgsLoading ? (
                                            <div className={styles.extraOrgsLoading}>Loading memberships…</div>
                                        ) : extraOrgs.length === 0 ? (
                                            <div className={styles.extraOrgsEmpty}>
                                                No additional organisations granted.
                                            </div>
                                        ) : (
                                            <ul className={styles.extraOrgsList}>
                                                {extraOrgs.map((m) => (
                                                    <li key={m.organisation} className={styles.extraOrgsItem}>
                                                        <span className={styles.extraOrgsBadge}>{m.organisation}</span>
                                                        <span className={styles.extraOrgsMeta}>
                                                            {m.grantedBy ? `by ${m.grantedBy}` : ''}
                                                            {m.grantedAt
                                                                ? ` on ${new Date(m.grantedAt).toLocaleDateString(
                                                                      'en-GB',
                                                                      {
                                                                          day: '2-digit',
                                                                          month: 'short',
                                                                          year: 'numeric',
                                                                      },
                                                                  )}`
                                                                : ''}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className={styles.extraOrgsRevoke}
                                                            onClick={() => handleRevokeOrg(m.organisation)}
                                                        >
                                                            Revoke
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}

                                        <div className={styles.extraOrgsGrantRow}>
                                            <select
                                                className={styles.extraOrgsSelect}
                                                value={orgToGrant}
                                                onChange={(e) => setOrgToGrant(e.target.value)}
                                                disabled={isGrantingOrg}
                                            >
                                                <option value="">Select an organisation to grant…</option>
                                                {organisations
                                                    .filter(
                                                        (org) =>
                                                            org !== user.organisation &&
                                                            !extraOrgs.some((m) => m.organisation === org),
                                                    )
                                                    .map((org) => (
                                                        <option key={org} value={org}>
                                                            {org}
                                                        </option>
                                                    ))}
                                            </select>
                                            <button
                                                type="button"
                                                className={styles.extraOrgsGrantButton}
                                                onClick={handleGrantOrg}
                                                disabled={!orgToGrant || isGrantingOrg}
                                            >
                                                {isGrantingOrg ? 'Granting…' : 'Grant Access'}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className={styles.modalActions}>
                                <button
                                    onClick={handleToggleStatus}
                                    className={`${styles.actionButton} ${user.enabled ? styles.disable : styles.enable}`}
                                >
                                    {user.enabled ? 'Disable User' : 'Enable User'}
                                </button>
                                <button onClick={() => setIsEditing(true)} className={styles.editButton}>
                                    Edit User
                                </button>
                                <Link
                                    to={`/admin/user-activity?user=${encodeURIComponent(user.email)}`}
                                    className={styles.activityLink}
                                    title="Pages viewed and searches made by this user"
                                >
                                    <FaHistory /> View activity
                                </Link>
                                <button onClick={handleDeleteUser} className={styles.deleteButton}>
                                    Delete User
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}

export default UserDetailModal
